import type { Recipe, RecipeLink } from '@recipes/contracts';
import { formatMinutes } from '@recipes/domain';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  cn,
} from '@recipes/ui';
import { ArrowLeft, BookMarked, TimerIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { currentLocale } from '@/lib/i18n';
import { timerStore } from '@/lib/timers/timer-store';
import { useRecipe } from '../queries';
import { ScaledIngredients } from '../scaled-ingredients';
import { rememberLeftPlace } from './left-place';

/** Router state of a visit that started at a link in another recipe: "back" is one step back. */
interface LinkedVisitState {
  fromRecipe?: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface LinkedRecipeChipProps {
  link: RecipeLink;
  /** The recipe the link is shown on – the way back from the linked one. */
  fromRecipeId?: string;
  /** Open in place (cooking mode) instead of going to the recipe page. */
  onOpen?: (link: RecipeLink) => void;
  size?: 'default' | 'lg';
  className?: string;
}

/** A base recipe used by an ingredient line or a step: its title, one tap away. */
export function LinkedRecipeChip({
  link,
  fromRecipeId,
  onOpen,
  size = 'default',
  className,
}: LinkedRecipeChipProps) {
  const { t } = useTranslation();
  const classes = cn(
    'inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 font-medium text-foreground underline-offset-4 hover:bg-primary/20 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    size === 'lg' ? 'min-h-[44px] px-4 text-base' : 'min-h-[28px] px-2.5 py-0.5 text-sm',
    className,
  );
  const content = (
    <>
      <BookMarked className={cn('shrink-0', size === 'lg' ? 'size-5' : 'size-3.5')} aria-hidden />
      <span className="sr-only">{t('baseRecipes.link.prefix')} </span>
      <span className="truncate">{link.title}</span>
    </>
  );
  if (onOpen) {
    return (
      <button
        type="button"
        className={classes}
        onClick={() => onOpen(link)}
        data-testid="linked-recipe"
        data-recipe-id={link.id}
      >
        {content}
      </button>
    );
  }
  const search = fromRecipeId ? `?from=${fromRecipeId}` : '';
  return (
    <Link
      to={`/recipes/${link.id}${search}`}
      state={{ fromRecipe: true } satisfies LinkedVisitState}
      onClick={() => fromRecipeId && rememberLeftPlace(fromRecipeId)}
      className={classes}
      data-testid="linked-recipe"
      data-recipe-id={link.id}
    >
      {content}
    </Link>
  );
}

/**
 * On a recipe that was opened from a link in another one: the way back, always in view. One step
 * back in the history when the visit started in the app – the main recipe opens where the reader
 * left it – and a plain link after a reload or in a new tab.
 */
export function BackToRecipeBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const from = params.get('from');
  const fromId = from && UUID.test(from) ? from : undefined;
  const origin = useRecipe(fromId);
  if (!fromId) return null;
  const title = origin.data?.recipe.title;
  const cameFromApp = (location.state as LinkedVisitState | null)?.fromRecipe === true;
  return (
    <div className="sticky top-0 z-20 -mx-4 mb-3 border-b bg-background/95 px-4 py-2 backdrop-blur md:-mx-6 md:px-6">
      <Button
        variant="secondary"
        onClick={() => (cameFromApp ? navigate(-1) : navigate(`/recipes/${fromId}`))}
        className="max-w-full"
        data-testid="back-to-recipe"
      >
        <ArrowLeft aria-hidden />
        <span className="truncate">
          {title ? t('baseRecipes.back.toTitle', { title }) : t('baseRecipes.back.generic')}
        </span>
      </Button>
    </div>
  );
}

/** "Base recipe" marker for headers, rows and cards. */
export function BaseRecipeBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={className} data-testid="base-recipe-badge">
      <BookMarked className="mr-1 size-3" aria-hidden />
      {t('baseRecipes.badge')}
    </Badge>
  );
}

/** On a base recipe: the recipes that use it. */
export function UsedInSection({ recipe }: { recipe: Pick<Recipe, 'usedIn'> }) {
  const { t } = useTranslation();
  const usedIn = recipe.usedIn ?? [];
  if (usedIn.length === 0) return null;
  return (
    <section aria-labelledby="used-in-heading" data-testid="used-in">
      <h2 id="used-in-heading" className="mb-2 text-xl font-semibold">
        {t('baseRecipes.usedIn.title')}
      </h2>
      <ul className="divide-y rounded-lg border">
        {usedIn.map((link) => (
          <li key={link.id}>
            <Link
              to={`/recipes/${link.id}`}
              className="flex min-h-[44px] items-center justify-between gap-2 p-3 hover:bg-accent"
            >
              <span className="min-w-0 truncate">{link.title}</span>
              {link.isBaseRecipe && <BaseRecipeBadge />}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface LinkedRecipeSheetProps {
  /** The base recipes opened one inside the other; the last one is shown. Empty = closed. */
  stack: RecipeLink[];
  onStackChange: (stack: RecipeLink[]) => void;
  /** Title of the recipe being cooked – where "close" leads back to. */
  originTitle: string;
}

/**
 * A base recipe while cooking, on top of the cooking mode: step, timers and ticked ingredients of
 * the main recipe stay as they are, and closing the sheet is the way back.
 */
export function LinkedRecipeSheet({ stack, onStackChange, originTitle }: LinkedRecipeSheetProps) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const current = stack[stack.length - 1];
  const query = useRecipe(current?.id);
  const recipe = query.data?.recipe;
  const open = (link: RecipeLink) => {
    // A → B → A would go round in circles: step back to it instead.
    const at = stack.findIndex((entry) => entry.id === link.id);
    onStackChange(at >= 0 ? stack.slice(0, at + 1) : [...stack, link]);
  };
  const stepCount = recipe?.stepSections.reduce((n, section) => n + section.steps.length, 0) ?? 0;
  let stepNumber = 0;

  return (
    <Dialog open={stack.length > 0} onOpenChange={(next) => !next && onStackChange([])}>
      <DialogContent
        fullscreenOnMobile
        closeLabel={t('baseRecipes.sheet.close', { title: originTitle })}
        className="sm:max-w-2xl"
        data-testid="linked-recipe-sheet"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <BookMarked className="size-5 shrink-0" aria-hidden />
            <span className="min-w-0">{recipe?.title ?? current?.title}</span>
          </DialogTitle>
          <DialogDescription>
            {recipe
              ? t('baseRecipes.sheet.yields', {
                  servings: `${recipe.servings} ${recipe.servingsLabel ?? t('common.servings')}`,
                })
              : t('baseRecipes.badge')}
          </DialogDescription>
        </DialogHeader>
        {stack.length > 1 && (
          <Button
            variant="outline"
            size="lg"
            className="self-start"
            onClick={() => onStackChange(stack.slice(0, -1))}
            data-testid="linked-recipe-sheet-back"
          >
            <ArrowLeft aria-hidden />
            {t('baseRecipes.back.toTitle', { title: stack[stack.length - 2]!.title })}
          </Button>
        )}
        {query.isPending ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-8" />
            <Skeleton className="h-32" />
          </div>
        ) : !recipe ? (
          <p className="text-muted-foreground" role="alert">
            {t('baseRecipes.sheet.unavailable')}
          </p>
        ) : (
          <div className="space-y-5 overflow-y-auto" lang={recipe.language ?? undefined}>
            <section aria-labelledby="linked-ingredients-heading">
              <h3 id="linked-ingredients-heading" className="mb-1 text-lg font-semibold">
                {t('recipes.detail.ingredients')}
              </h3>
              <ScaledIngredients recipe={recipe} size="lg" onOpenLinked={open} />
            </section>
            <section aria-labelledby="linked-steps-heading">
              <h3 id="linked-steps-heading" className="mb-2 text-lg font-semibold">
                {t('recipes.detail.steps')}
              </h3>
              {recipe.stepSections.map((section) => (
                <div key={section.id} className="mb-3">
                  {section.title && <h4 className="mb-1 font-semibold">{section.title}</h4>}
                  <ol className="space-y-3">
                    {section.steps.map((step) => {
                      stepNumber += 1;
                      const n = stepNumber;
                      return (
                        <li key={step.id} className="flex gap-3" data-testid="linked-step">
                          <span
                            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
                            aria-hidden
                          >
                            {n}
                          </span>
                          <div className="min-w-0 flex-1 space-y-2">
                            <span className="sr-only">{t('recipes.detail.stepN', { n })}</span>
                            <p className="whitespace-pre-wrap text-lg leading-relaxed">
                              {step.instruction}
                            </p>
                            {step.durationSeconds ? (
                              <Button
                                variant="secondary"
                                size="lg"
                                onClick={() =>
                                  timerStore.start({
                                    label: `${recipe.title} · ${t('recipes.detail.stepN', { n })}`,
                                    durationSeconds: step.durationSeconds!,
                                    recipeId: recipe.id,
                                    stepId: step.id,
                                  })
                                }
                              >
                                <TimerIcon aria-hidden />
                                {t('cooking.startTimerFor', {
                                  duration: formatMinutes(
                                    Math.max(1, Math.round(step.durationSeconds / 60)),
                                    locale,
                                  ),
                                })}
                              </Button>
                            ) : null}
                            {(step.linkedRecipes ?? []).length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {step.linkedRecipes!.map((link) => (
                                  <LinkedRecipeChip
                                    key={link.id}
                                    link={link}
                                    onOpen={open}
                                    size="lg"
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              ))}
              {stepCount === 0 && (
                <p className="text-sm text-muted-foreground">{t('recipes.detail.noSteps')}</p>
              )}
            </section>
            {recipe.notes && (
              <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm">
                {recipe.notes}
              </p>
            )}
          </div>
        )}
        <Button size="xl" onClick={() => onStackChange([])} data-testid="linked-recipe-sheet-close">
          <ArrowLeft aria-hidden />
          {t('baseRecipes.back.toTitle', { title: originTitle })}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
